import { useCallback, useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { ScheduleOrg } from '@wetags/schedule';
import type { ConfigType } from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDateTime, FormatDateTimeOptions } from '@frontend/schedule-helpers';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

const enableStatesOfCF = [State.ACTIVE, State.TRIAL, State.PROMOTE];

type SchedulingCF = 'quickfill' | 'apptreminders-automated' | 'forms';

export const useSchedulingLocationInfo = () => {
  const state = useAppScopeStore();
  const { getCustomizationFlag } = useAppFlagStore();
  const { getCustomizationFlagLocationMap } = useScopedAppFlagStore();
  const [isFeatureReadinessProjectEnabled, isFeatureReadinessProjectWetagLoading] = ScheduleOrg.useWetag(
    'feature-readiness-project-enablement'
  );
  const { readyLocationIds: newOSReadyLocationIds } = SchedulerV3.Helpers.useOnlineSchedulingFeatureReadiness({
    options: {
      enabled: isFeatureReadinessProjectEnabled && !isFeatureReadinessProjectWetagLoading,
    },
  });

  const getLocationTimeZone = useCallback(
    (locationId: string) => {
      const location = state.accessibleLocationData?.[locationId];
      return location?.timezone || '';
    },
    [state.accessibleLocationData]
  );

  const formatLocationTime = useCallback(
    (inputDateTime: ConfigType, locationId: string, options?: Omit<FormatDateTimeOptions, 'fromTimezone'>) => {
      if (!locationId) {
        console.error('Location ID is required for date-time conversion');
        return '';
      }

      return formatDateTime(inputDateTime, { ...options, fromTimezone: getLocationTimeZone(locationId) });
    },
    [getLocationTimeZone]
  );

  const getCFLocationMap = useCallback(
    (feature: SchedulingCF) => {
      const cfLocationMap = getCustomizationFlagLocationMap(feature) || {};

      return Object.fromEntries(
        Object.entries(cfLocationMap).map(([locationId, cf]) => [
          locationId,
          cf?.state ? enableStatesOfCF.includes(cf.state) : false,
        ])
      );
    },
    [getCustomizationFlagLocationMap]
  );

  const getCFValueByLocationId = useCallback(
    (locationId: string, flagName: SchedulingCF) => {
      const cfMap = getCustomizationFlag({ locationIds: [locationId], flagName }) || {};
      const cfState = (cfMap[0] ?? {}).state;

      return cfState ? enableStatesOfCF.includes(cfState) : false;
    },
    [getCustomizationFlag]
  );

  const oldOSEnabledLocationIds = useMemo(() => {
    const oldOSCFLocationMap = getCFLocationMap('onlinescheduling' as any); // NOTE: temporarily using any to bypass the type issue, as we were going to remove the CF-based approach for online scheduling readiness.
    return Object.keys(oldOSCFLocationMap).filter((locationId) => !!oldOSCFLocationMap[locationId]);
  }, [getCFLocationMap]);

  const getOnlineSchedulingReadyLocationIds = useCallback(() => {
    if (!isFeatureReadinessProjectWetagLoading && isFeatureReadinessProjectEnabled) {
      return newOSReadyLocationIds;
    }

    return oldOSEnabledLocationIds;
  }, [
    isFeatureReadinessProjectEnabled,
    isFeatureReadinessProjectWetagLoading,
    newOSReadyLocationIds,
    oldOSEnabledLocationIds,
  ]);

  return {
    isMultiLocation: !state.isSingleTypeScope,
    parentLocationId: state.selectedGroupId,
    getLocationTimeZone,
    formatLocationTime,
    getCFLocationMap,
    getCFValueByLocationId,
    getOnlineSchedulingReadyLocationIds,
    ...state,
  };
};
